import React, { Suspense, lazy } from 'react'
import { Route, Routes, Navigate, useParams, useLocation } from 'react-router-dom'
import HomePage from './components/Pages/HomePage'
import AboutPage from './components/Pages/AboutPage'
import PrivacyPolicyPage from './components/Pages/PrivacyPolicyPage'
import TermsOfServicePage from './components/Pages/TermsOfServicePage'
import HowItWorksPage from './components/Pages/Marketing/HowItWorksPage'
import WeddingsPage from './components/Pages/Marketing/WeddingsPage'
import VenuesPage from './components/Pages/Marketing/VenuesPage'
import MeetupsPage from './components/Pages/Marketing/MeetupsPage'
import LoginPage from './components/Pages/LoginPage'
import LogoutPage from './components/Pages/LogoutPage'
import SignupPage from './components/Pages/SignupPage'
import ForgotPasswordPage from './components/Pages/ForgotPasswordPage'
import ResetPasswordPage from './components/Pages/ResetPasswordPage'
import VerifyEmailPage from './components/Pages/VerifyEmailPage'
import EventHostInvitePage from './components/Pages/EventHostInvitePage'
import GoogleOauthCallbackPage from './components/Pages/GoogleOauthCallbackPage'
import GuestExpiredPage from './components/Pages/GuestExpiredPage'
import EventsPage from './components/Pages/EventsPage'
import AppsPage from './components/Pages/AppsPage'
import EventPage from './components/Pages/EventPage'
import CreateEventPage from './components/Pages/CreateEventPage'
import CreateEventBySlugPage from './components/Pages/CreateEventBySlugPage'
import ViewProfilePage from './components/Pages/ViewProfilePage'
import NearbyPage from './components/Pages/NearbyPage'
import VenuePage from './components/Pages/VenuePage'
import ChatPage from './components/Pages/ChatPage'
import ROUTES from './routesConfig'
import ProtectedLayout from './ProtectedLayout'
import GuestReestablishGate from './components/Auth/GuestReestablishGate'
import MerchantPrivilegesLayout from './MerchantPrivilegesLayout'

/** Legacy nested /e/slug/... URLs → front door /e/slug (query preserved) */
const StripEventDeepLink = () => {
  const { eventSlug } = useParams()
  const { search } = useLocation()
  return <Navigate to={`${ROUTES.EVENT_PAGE(eventSlug)}${search}`} replace />
}

const ScanPage = lazy(() => import('./components/Pages/ScanPage'))
const GuestSignupPage = lazy(() => import('./components/Pages/GuestSignupPage'))
const UpdateProfilePage = lazy(() => import('./components/Pages/UpdateProfilePage'))
const MerchantDashboardPage = lazy(() => import('./components/Pages/MerchantDashboardPage'))
const MerchantProfilePage = lazy(() => import('./components/Pages/MerchantProfilePage'))
const MerchantProfileEditPage = lazy(() => import('./components/Pages/MerchantProfileEditPage'))
const MerchantFavoriteAppsEditPage = lazy(() => import('./components/Pages/MerchantFavoriteAppsEditPage'))
const MerchantAppViewPage = lazy(() => import('./components/Pages/MerchantAppViewPage'))
const MerchantVenueCreatePage = lazy(() => import('./components/Pages/MerchantVenueCreatePage'))
const MerchantGettingStartedPage = lazy(() => import('./components/Pages/MerchantGettingStartedPage'))
const MerchantVenueShowPage = lazy(() => import('./components/Pages/MerchantVenueShowPage'))
const MerchantVenueEditPage = lazy(() => import('./components/Pages/MerchantVenueEditPage'))
const MerchantVenueAppsEditPage = lazy(() => import('./components/Pages/MerchantVenueAppsEditPage'))
const MerchantVenueAppsDiscoverPage = lazy(() => import('./components/Pages/MerchantVenueAppsDiscoverPage'))
const MerchantVenueQrPage = lazy(() => import('./components/Pages/MerchantVenueQrPage'))

const AnearRoutes = () => {
  return (
    <Suspense fallback={null}>
      <GuestReestablishGate />
      <Routes>
        <Route path={ROUTES.HOME}   element={<HomePage />} />
        <Route path={ROUTES.ROOT}   element={<HomePage />} />
        <Route path={ROUTES.SCAN}   element={<ScanPage />} />
        <Route path={ROUTES.ABOUT}  element={<AboutPage />} />
        <Route path={ROUTES.PRIVACY} element={<PrivacyPolicyPage />} />
        <Route path={ROUTES.TERMS} element={<TermsOfServicePage />} />
        <Route path={ROUTES.HOW_IT_WORKS} element={<HowItWorksPage />} />
        <Route path={ROUTES.WEDDINGS}     element={<WeddingsPage />} />
        <Route path={ROUTES.VENUES}       element={<VenuesPage />} />
        <Route path={ROUTES.MEETUPS}      element={<MeetupsPage />} />
        <Route path={ROUTES.LOGIN}  element={<LoginPage />} />
        <Route path={ROUTES.LOGOUT} element={<LogoutPage />} />
        <Route path={ROUTES.SIGNUP} element={<SignupPage />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
        <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
        <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmailPage />} />
        <Route path="/ops" element={<Navigate to={ROUTES.APPS} replace />} />
        <Route path={ROUTES.EVENT_HOST_INVITE} element={<EventHostInvitePage />} />
        <Route path={ROUTES.GOOGLE_OAUTH_CALLBACK} element={<GoogleOauthCallbackPage />} />
        <Route path={ROUTES.GUEST_SIGNUP} element={<GuestSignupPage />} />
        <Route path={ROUTES.GUEST_EXPIRED} element={<GuestExpiredPage />} />
        {/* Public Apps listing should be viewable without auth */}
        <Route path={ROUTES.APPS}   element={<AppsPage />} />
        {/* Public route to create event by app slug - redirects to protected create event page */}
        <Route path={ROUTES.CREATE_EVENT_BY_SLUG()} element={<CreateEventBySlugPage />} />

        <Route element={<ProtectedLayout />}>
          <Route path={ROUTES.CREATE_EVENT()}      element={<CreateEventPage />} />
          <Route path={ROUTES.PROFILE}             element={<ViewProfilePage />} />
          <Route path={ROUTES.UPDATE_PROFILE}      element={<UpdateProfilePage />} />
          <Route path={ROUTES.CHAT}                element={<ChatPage />} />
          <Route path={ROUTES.EVENTS}              element={<EventsPage />} />
          <Route path={ROUTES.NEARBY}              element={<NearbyPage />} />
          <Route path={ROUTES.EVENT_PAGE(undefined, { matchNested: true })} element={<StripEventDeepLink />} />
          <Route path={ROUTES.EVENT_PAGE()} element={<EventPage />} />
          <Route path={ROUTES.VENUE_PAGE()}        element={<VenuePage />} />

          <Route element={<MerchantPrivilegesLayout />}>
            <Route path={ROUTES.MERCHANT_DASHBOARD} element={<MerchantDashboardPage />} />
            <Route path={ROUTES.MERCHANT_APP_VIEW()} element={<MerchantAppViewPage />} />
            <Route path={ROUTES.MERCHANT_VENUE_CREATE} element={<MerchantVenueCreatePage />} />
            <Route path={ROUTES.MERCHANT_GETTING_STARTED} element={<MerchantGettingStartedPage />} />
            <Route path={ROUTES.MERCHANT_VENUE_SHOW()} element={<MerchantVenueShowPage />} />
            <Route path={ROUTES.MERCHANT_VENUE_EDIT()} element={<MerchantVenueEditPage />} />
            <Route path={ROUTES.MERCHANT_VENUE_APPS_DISCOVER()} element={<MerchantVenueAppsDiscoverPage />} />
            <Route path={ROUTES.MERCHANT_VENUE_APPS_EDIT()} element={<MerchantVenueAppsEditPage />} />
            <Route path={ROUTES.MERCHANT_VENUE_QR()} element={<MerchantVenueQrPage />} />
            <Route path={ROUTES.MERCHANT_PROFILE} element={<MerchantProfilePage />} />
            <Route path={ROUTES.MERCHANT_PROFILE_EDIT} element={<MerchantProfileEditPage />} />
            <Route path={ROUTES.MERCHANT_FAVORITE_APPS_EDIT} element={<MerchantFavoriteAppsEditPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  )
}

export default AnearRoutes
